import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AuthProviders, { oauthErrorMessage } from '../components/AuthProviders';
import { api, isDemoMode } from '../lib/api';

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(() => oauthErrorMessage(params.get('oauth_error')));
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.login(email.trim(), password);
      nav('/dashboard');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="page auth-wrap">
        <section className="auth-card">
          <h1>Welcome back</h1>
          <p className="auth-lead">Choose the fastest way into your workspace.</p>
          <AuthProviders returnTo="/dashboard" failureTo="/login" onError={setError} />
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="auth-divider"><span>or</span></div>
          <button
            className="auth-email-toggle"
            type="button"
            aria-expanded={showEmail}
            onClick={() => setShowEmail((open) => !open)}
          >
            {showEmail ? 'Hide email sign-in' : 'Continue with email'}
          </button>
          {showEmail && (
            <form className="auth-email-form" onSubmit={submit}>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
                {busy ? 'Signing in…' : 'Log in with email'}
              </button>
            </form>
          )}
          <p className="auth-alt">No account? <Link to="/signup">Start free</Link></p>
          {isDemoMode() && (
            <p className="demo-hint">OFFLINE DEMO — every sign-in option opens the local demo.</p>
          )}
        </section>
      </div>
    </>
  );
}
